import React, {Fragment, useState, useEffect} from "react";
import Popover from "@mui/material/Popover";
import IsoIcon from '@mui/icons-material/Iso';
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../pages/HomePage/HomePage";
import CustomCurrencyInput from "./CustomCurrencyInput";
import classnames from "classnames";
import Stack from "@mui/material/Stack";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PercentIcon from '@mui/icons-material/Percent';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";

export const CostCalculator = (props) => {
    const {
        state,
        setState,
        isLoading,
        handleOnFocus,
        handleBlur
    } = props
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const [anchorEl, setAnchorEl] = useState(null);

    const hasInvoiceBase = Boolean(state.invoice_plus || state.invoice_minus)
    const hasMSRPBase = Boolean(state.msrp_plus || state.msrp_minus || state.msrp_percentage)

    useEffect(function setCost() {
        let newCost = state.cost

        if (hasInvoiceBase) {
            newCost = Number(state.invoice) + Number(state.invoice_plus) - Number(state.invoice_minus)
        } else if (hasMSRPBase) {
            if (state.msrp_percentage) {
                const percentage = Number(state.msrp_percentage) / 100
                newCost = Number(state.msrp) - (Number(state.msrp) * percentage)
            } else {
                newCost = Number(state.msrp) + Number(state.msrp_plus) - Number(state.msrp_minus)
            }
        }

        if (newCost !== state.cost) {
            setState({...state, cost: newCost})
        }
    },[state, setState])

    const handleActionClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleActionClose = () => {
        setAnchorEl(null);
    };

    const setFieldValues = (field, value) => {
        const fieldValues = {
            invoice_plus: '',
            invoice_minus: '',
            msrp_plus: '',
            msrp_minus: '',
            msrp_percentage: ''
        }

        fieldValues[field] = value
        setState({...state, ...fieldValues})
    }

    const hasInput = () => (
        Boolean(
            state.msrp_plus ||
            state.msrp_minus ||
            state.msrp_percentage ||
            state.invoice_plus ||
            state.invoice_minus
        )
    )

    const isInvoiceDisabled = Boolean(!state.invoice)
    const isMsrpDisabled = Boolean(!state.msrp)

    return (
        <React.Fragment>
            <Tooltip title="Cost Calculator" arrow placement={'top'}>
                <IsoIcon
                    sx={{
                        cursor: (!isMsrpDisabled) ? "pointer" : '',
                        color: hasInput() ? "#2267CE" : ""
                }}
                    onClick={(e) => {
                        if (!isMsrpDisabled) {
                            handleActionClick(e)
                        }
                    }}
                    onFocus={(e) => {
                        handleOnFocus && handleOnFocus(e)
                    }}
                    onBlur={(e) => {
                        handleBlur && handleBlur(e)
                    }}
                />
            </Tooltip>
            <Popover
                onClick={(e) => e.stopPropagation()}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleActionClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
            >
                <Box
                    sx={{ height: "320px", width: "400px" }}
                    className={classnames('CalcPageInvoiceCalculator', {
                    mobile: isTabletOrMobile
                })}>
                    <Typography
                        gutterBottom
                        variant="h6"
                        component="div"
                        className={'header'}
                    >
                        Cost Calculator
                    </Typography>
                    <Divider className={'divider'}/>
                    <Stack
                        direction={'column'}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <div
                            className={'payment-row'}
                        >
                        <span
                            className={'payment-row_name'}
                        >
                            MSRP:
                        </span>
                            <CustomCurrencyInput
                                className={classnames('payment-row_value', {
                                    loading: isLoading,
                                    isSelected: hasMSRPBase,
                                    disabled: true,
                                })}
                                prefix="$"
                                value={state.msrp}
                                onChange={(value) => setState({...state, msrp: value})}
                                disabled={true}
                            />
                        </div>

                        <div
                            className={'payment-row'}
                        >
                        <span
                            className={'payment-row_name'}
                        >
                        </span>
                            <Fragment>
                                <RemoveIcon/>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading,
                                        isSelected: Boolean(state.msrp_minus),
                                    })}
                                    value={state.msrp_minus}
                                    onChange={(value) => setFieldValues("msrp_minus", value)}
                                    disabled={isMsrpDisabled}
                                    allowBlank={true}
                                />
                            </Fragment>
                            <Fragment>
                                <AddIcon/>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading,
                                        isSelected: Boolean(state.msrp_plus),
                                    })}
                                    value={state.msrp_plus}
                                    onChange={(value) => setFieldValues("msrp_plus", value)}
                                    disabled={isMsrpDisabled}
                                    allowBlank={true}
                                />
                            </Fragment>
                        </div>
                        <div
                            className={'payment-row'}
                            style={{justifyContent: 'end'}}
                        >
                            <Fragment>
                                <RemoveIcon/>
                                <PercentIcon sx={{ marginRight: '20px'}}/>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading,
                                        isSelected: Boolean(state.msrp_percentage),
                                    })}
                                    value={state.msrp_percentage}
                                    onChange={(value) => setFieldValues("msrp_percentage", value)}
                                    disabled={isMsrpDisabled}
                                    allowBlank={true}
                                    InputProps={
                                        {
                                            endAdornment: (
                                                <InputAdornment
                                                    sx={{fontSize: '10px'}}
                                                    position="start">%
                                                </InputAdornment>
                                            ),
                                        }
                                    }
                                />
                            </Fragment>
                        </div>
                        <div
                            className={'payment-row'}
                        >
                        <span
                            className={'payment-row_name'}
                        >
                            Invoice:
                        </span>
                            <CustomCurrencyInput
                                className={classnames('payment-row_value', {
                                    loading: isLoading,
                                    isSelected: hasInvoiceBase,
                                    disabled: true,
                                })}
                                prefix="$"
                                value={state.invoice}
                                onChange={(value) => setState({...state, invoice: value})}
                                allowBlank={true}
                                disabled={true}
                            />
                        </div>

                        <div
                            className={'payment-row'}
                        >
                        <span
                            className={'payment-row_name'}
                        >

                        </span>
                            <Fragment>
                                <RemoveIcon/>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading,
                                        isSelected: Boolean(state.invoice_minus),
                                    })}
                                    value={state.invoice_minus}
                                    onChange={(value) => setFieldValues("invoice_minus", value)}
                                    disabled={isInvoiceDisabled}
                                    allowBlank={true}
                                />
                            </Fragment>
                            <Fragment>
                                <AddIcon/>
                                <CustomCurrencyInput
                                    className={classnames('payment-row_value', {
                                        loading: isLoading,
                                        isSelected: Boolean(state.invoice_plus),
                                    })}
                                    value={state.invoice_plus}
                                    onChange={(value) => setFieldValues("invoice_plus", value)}
                                    disabled={isInvoiceDisabled}
                                    allowBlank={true}
                                />
                            </Fragment>
                        </div>
                    </Stack>
                    <Divider className={'divider'} sx={{ marginTop: '10px'}}/>
                    <div
                        className={'payment-row'}
                    >
                        <span
                            className={'payment-row_name'}
                        >
                            Cost:
                        </span>
                        <React.Fragment>
                            <CustomCurrencyInput
                                className={classnames('payment-row_value disabled', {
                                    loading: isLoading,
                                    isSelected: true
                                })}
                                value={state.cost}
                                disabled={true}
                            />
                        </React.Fragment>
                    </div>
                </Box
                >

            </Popover>
        </React.Fragment>
    )
}